import React from 'react';
import cn from 'classnames';
import { PaymentMethod } from '@wix/cashier-common/dist/src/enums/payments/PaymentMethod';
import { DH } from '../../../../utils/DataHook';
import { isTranslated, t } from '../../../../utils/i18n';
import {
  AfterpayDescription,
  ClearpayDescription,
  LaybuyDescription,
} from './StaticInstallmentsDescription';
import * as s from './styles.scss';
import {
  Overlay,
  Modal,
  Container,
  CloseButton,
  Body,
} from '../../../ModalLayout';
import { DeviceType } from '../../../../types/DeviceType';
import { DeviceType as DeviceTypeEnum } from '@wix/cashier-common/dist/src/enums/DeviceType';
import { PaymentMethodIcon } from '../../../PaymentMethodIcon';

export const Title: React.FunctionComponent<{
  method: string;
  label: string;
  iconUrl?: string;
  isModal?: boolean;
  amount?: string;
  locale?: string;
}> = ({ method, label, iconUrl, isModal, locale }) => {
  let description;

  const descriptionKey = `wallet.pay.with.description.${method}`;
  if (isTranslated(descriptionKey)) {
    description = t(descriptionKey);
  }

  return (
    <div
      className={cn({
        [s.title]: true,
        [s.titleWithIcon]: !!iconUrl,
        [s.modalTheme]: isModal,
      })}
      data-hook={DH.PaymentMethod.Wallet.Title}
    >
      <div className={s.titleText}>
        <span>
          <span>{t('wallet.pay.with')}</span>
          <span>&nbsp;</span>
          <span>{label}</span>
        </span>
        {description && (
          <span className={s.titleDescription}>&ensp;{description}</span>
        )}
      </div>
      {isModal && (
        <div className={s.titleIcon} data-hook={DH.PaymentMethod.Wallet.Icon}>
          <PaymentMethodIcon
            paymentMethod={method as PaymentMethod}
            url={iconUrl}
            lang={locale}
          />
        </div>
      )}
    </div>
  );
};
Title.displayName = 'Title';

export const Description: React.FunctionComponent<{
  methodName: PaymentMethod;
  amount?: string;
  currency?: string;
  country?: string;
  deviceType?: DeviceType;
  isSinglePaymentMethodOption?: boolean;
}> = ({
  methodName,
  isSinglePaymentMethodOption,
  amount,
  currency,
  country,
  deviceType,
}) => {
  let description;
  switch (methodName) {
    case PaymentMethod.Afterpay:
      description = (
        <AfterpayDescription
          deviceType={deviceType}
          amount={amount}
          currency={currency}
          country={country}
          isSinglePaymentMethodOption={isSinglePaymentMethodOption}
        />
      );
      break;
    case PaymentMethod.Clearpay:
      description = (
        <ClearpayDescription
          deviceType={deviceType}
          amount={amount}
          currency={currency}
          country={country}
          isSinglePaymentMethodOption={isSinglePaymentMethodOption}
        />
      );
      break;
    case PaymentMethod.Laybuy:
      description = (
        <LaybuyDescription
          deviceType={deviceType}
          amount={amount}
          currency={currency}
          country={country}
          isSinglePaymentMethodOption={isSinglePaymentMethodOption}
        />
      );
      break;
    default:
      return null;
  }

  return (
    <div data-hook={DH.PaymentMethod.Wallet.Description}>{description}</div>
  );
};
Description.displayName = 'Description';

export const Details: React.FunctionComponent<{
  method: string;
  label: string;
  deviceType: DeviceType;
  withRedirect: boolean;
}> = ({ method, label, deviceType, withRedirect }) => {
  let forKey = 'payments.wallet.redirect.description.short';
  const customPMSpecificKey = `payments.${method}.custom.description`;
  const customGeneralKey = `payments.custom.description`;

  if (isTranslated(customPMSpecificKey)) {
    forKey = customPMSpecificKey;
  } else if (isTranslated(customGeneralKey) && !withRedirect) {
    forKey = customGeneralKey;
  }

  const details = t(forKey, {
    paymentMethodLabel: label,
  });

  return (
    <div
      className={cn(s.detailsContent, {
        [s.isMobile]: deviceType === 'mobile',
      })}
      data-hook={`cashier-payments-method-wallet-${method}-description`}
    >
      {details}
    </div>
  );
};
Details.displayName = 'Details';

export const RedirectIcon: React.FunctionComponent = () => {
  const redirectIcon =
    require('../../../../assets/img/redirect-icon.svg').default;
  return (
    <div className={s.redirectIcon}>
      <img src={redirectIcon} />
    </div>
  );
};
RedirectIcon.displayName = 'RedirectIcon';

export const SinglePaymentMethodViewIcon: React.FunctionComponent<{
  deviceType: DeviceType;
  paymentMethodIconUrl?: string;
  paymentMethodName?: PaymentMethod;
  locale?: string;
}> = ({ paymentMethodIconUrl, locale, paymentMethodName, deviceType }) => (
  <div
    className={cn(s.paymentMethodIcon, {
      [s.descriptionMobileView]: deviceType === DeviceTypeEnum.Mobile,
    })}
    data-hook={DH.PaymentMethod.Wallet.Icon}
  >
    <PaymentMethodIcon
      paymentMethod={paymentMethodName as PaymentMethod}
      url={paymentMethodIconUrl}
      lang={locale}
    />
  </div>
);
SinglePaymentMethodViewIcon.displayName = 'SinglePaymentMethodViewIcon';

export const SmallRedirectIcon: React.FunctionComponent = () => {
  const smallRedirectIcon =
    require('../../../../assets/img/small-redirect-icon.svg').default;
  return (
    <div className={s.smallRedirectIcon}>
      <img src={smallRedirectIcon} />
    </div>
  );
};
SmallRedirectIcon.displayName = 'SmallRedirectIcon';

const MORE_INFO_MODAL_SIZE_DEFAULT = {
  width: 615,
  height: 525,
};

const MORE_INFO_MODAL_SIZE_MOBILE = {
  width: '100%',
  height: '100%',
};

export const MoreInfoModal: React.FunctionComponent<{
  dataHook?: string;
  deviceType?: DeviceType;
  width?: number | string;
  height?: number | string;
  onClose(): void;
}> = (props) => {
  const { dataHook, deviceType, children, onClose } = props;
  const {
    width = MORE_INFO_MODAL_SIZE_DEFAULT.width,
    height = MORE_INFO_MODAL_SIZE_DEFAULT.height,
  } = deviceType === 'mobile' ? MORE_INFO_MODAL_SIZE_MOBILE : props;

  return (
    <Overlay dataHook={dataHook} onClick={onClose}>
      <Modal width={width} height={height} onClose={onClose}>
        <Container>
          <div className={s.closeButtonWrapper}>
            <CloseButton onClick={onClose} />
          </div>
          <Body>{children}</Body>
        </Container>
      </Modal>
    </Overlay>
  );
};
MoreInfoModal.displayName = 'MoreInfoModal';
